import { useEffect, useMemo, useState } from "react";
import { cn } from "lib/utils";
import Modal from "shared/Modal";
import Button from "shared/Button";
import SearchableSelect from "shared/SearchableSelect";
import { FORM_CONTROL } from "shared/tableStyles";
import Alert from "shared/Alert";
import { CONDUCTOR_ROLES, listMeetings } from "../api/MeetingsApi";
import type { ConductorRole, Meeting, MeetingEmployeeOption, MeetingPayload } from "../api/MeetingsApi";

interface MeetingFormProps {
  employees: MeetingEmployeeOption[];
  defaultEmployeeId?: number | null;
  initial?: Meeting | null;
  onSave: (data: MeetingPayload) => Promise<void>;
  onClose: () => void;
}

function toDateInput(value: string | null | undefined): string {
  if (!value) return "";
  return value.slice(0, 10);
}

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Label({ children, hint, htmlFor }: { children: React.ReactNode; hint?: string; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="block text-xs font-medium text-muted-foreground mb-1.5">
      {children}
      {hint && <span className="font-normal text-muted-foreground/70"> {hint}</span>}
    </label>
  );
}

export default function MeetingForm({ employees, defaultEmployeeId, initial, onSave, onClose }: MeetingFormProps) {
  const [employeeId, setEmployeeId] = useState<number | "">(initial?.employee_id ?? defaultEmployeeId ?? "");
  const [meetingDate, setMeetingDate] = useState(toDateInput(initial?.meeting_date) || todayLocal());
  const [conductorRole, setConductorRole] = useState<ConductorRole>(initial?.conductor_role ?? "HR");
  const [conductorEmployeeId, setConductorEmployeeId] = useState<number | "">(initial?.conductor_employee_id ?? "");
  const [beforeNotes, setBeforeNotes] = useState(initial?.before_meeting_notes ?? "");
  const [afterRemarks, setAfterRemarks] = useState(initial?.after_meeting_remarks ?? "");
  const [followUp, setFollowUp] = useState(initial?.follow_up ?? "");
  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: String(e.id), label: e.name, hint: e.designation })),
    [employees],
  );
  // Existing meetings for the chosen employee and day, so a likely double entry is flagged (not blocked).
  const [sameDay, setSameDay] = useState<Meeting[]>([]);
  useEffect(() => {
    if (!employeeId || !meetingDate) { setSameDay([]); return; }
    let cancelled = false;
    listMeetings({ employee_id: Number(employeeId), date_from: meetingDate, date_to: meetingDate, page_size: 5 })
      .then((r) => { if (!cancelled) setSameDay(r.items.filter((m) => m.id !== initial?.id)); })
      .catch(() => { if (!cancelled) setSameDay([]); });
    return () => { cancelled = true; };
  }, [employeeId, meetingDate, initial?.id]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const errs: Record<string, string> = {};
    if (!employeeId) errs.employeeId = "Choose an employee";
    if (!meetingDate) errs.meetingDate = "Choose a date";
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    setSubmitError("");
    try {
      const payload: MeetingPayload = {
        employee_id: Number(employeeId),
        meeting_date: meetingDate,
        conductor_role: conductorRole,
        conductor_employee_id: conductorEmployeeId !== "" ? Number(conductorEmployeeId) : null,
        before_meeting_notes: beforeNotes.trim() || null,
        after_meeting_remarks: afterRemarks.trim() || null,
        follow_up: followUp.trim() || null,
      };
      await onSave(payload);
      onClose();
    } catch {
      setSubmitError("Couldn't save the meeting. Check the details and try again.");
    } finally {
      setSaving(false);
    }
  }

  const close = saving ? undefined : onClose;

  return (
    <Modal
      size="lg"
      title={initial ? "Edit meeting" : "New meeting"}
      description="Record an issue or topic raised with an employee, and what came of it."
      onClick={close}
      closeButtonCLick={close}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => handleSubmit()} disabled={saving}>
            {saving ? "Saving…" : initial ? "Save changes" : "Add meeting"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="meeting-employee">Employee *</Label>
            <SearchableSelect
              id="meeting-employee"
              value={employeeId === "" ? "" : String(employeeId)}
              disabled={!!initial}
              onChange={(v) => { setEmployeeId(v === "" ? "" : Number(v)); setErrors((p) => ({ ...p, employeeId: "" })); }}
              options={employeeOptions}
              placeholder="Search employee…"
            />
            {errors.employeeId && <p className="text-xs text-destructive mt-1">{errors.employeeId}</p>}
          </div>
          <div>
            <Label>Date *</Label>
            <input
              type="date"
              value={meetingDate}
              onChange={(e) => { setMeetingDate(e.target.value); setErrors((p) => ({ ...p, meetingDate: "" })); }}
              className={cn(FORM_CONTROL, "w-full px-3 py-2.5")}
            />
            {errors.meetingDate && <p className="text-xs text-destructive mt-1">{errors.meetingDate}</p>}
          </div>
        </div>

        {sameDay.length > 0 && (
          <Alert tone="warning" role="status" className="text-sm">
            {sameDay.length === 1 ? "A meeting" : `${sameDay.length} meetings`} with this employee {sameDay.length === 1 ? "is" : "are"} already logged on this date
            {" "}({sameDay.map((m) => m.conductor_role).join(", ")}). You can still save this one if it is a separate meeting.
          </Alert>
        )}

        <div>
          <Label>Meeting with</Label>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Meeting with">
            {CONDUCTOR_ROLES.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={conductorRole === r}
                onClick={() => setConductorRole(r)}
                className={cn(
                  "px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors",
                  conductorRole === r
                    ? "border-primary text-primary bg-primary-soft"
                    : "border-border text-muted-foreground bg-surface hover:text-foreground",
                )}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="meeting-conducted-by" hint="(optional, the employee who held the meeting)">Conducted by</Label>
          <SearchableSelect
            id="meeting-conducted-by"
            value={conductorEmployeeId === "" ? "" : String(conductorEmployeeId)}
            onChange={(v) => setConductorEmployeeId(v === "" ? "" : Number(v))}
            options={employeeOptions}
            placeholder="Search employee…"
            clearLabel="Not specified"
          />
        </div>

        <div className="space-y-4">
          <div>
            <Label hint="(before)">Issue or topic to raise</Label>
            <textarea
              rows={4}
              value={beforeNotes}
              onChange={(e) => setBeforeNotes(e.target.value)}
              className={cn(FORM_CONTROL, "w-full px-3 py-2.5 resize-y")}
              placeholder="The problem or concern to discuss…"
            />
          </div>
          <div>
            <Label hint="(after)">Outcome</Label>
            <textarea
              rows={4}
              value={afterRemarks}
              onChange={(e) => setAfterRemarks(e.target.value)}
              className={cn(FORM_CONTROL, "w-full px-3 py-2.5 resize-y")}
              placeholder="What was discussed and agreed…"
            />
          </div>
          <div>
            <Label hint="(next steps)">Follow-up</Label>
            <textarea
              rows={4}
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              className={cn(FORM_CONTROL, "w-full px-3 py-2.5 resize-y")}
              placeholder="Actions, owners and when to check back…"
            />
          </div>
        </div>

        {submitError && <p role="alert" className="text-xs text-destructive">{submitError}</p>}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
